import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, User, Lock, Mail, Building, Save, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountSettingsPage = () => {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  // Profile form
  const [profile, setProfile] = useState({
    businessName: user?.business_name || '',
    email: user?.email || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password form
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      await axios.put(`${API}/auth/profile`, {
        business_name: profile.businessName,
        email: profile.email
      });
      toast.success('Profile updated successfully');
      
      // If email changed, user needs to re-login
      if (profile.email !== user?.email) {
        toast.info('Please log in again with your new email');
        logout();
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwords.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setSavingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.currentPassword,
        new_password: passwords.newPassword
      });
      toast.success('Password changed successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>

        <div className="mb-8">
          <h1 className="font-dashboard-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Account Settings
          </h1>
          <p className="text-[var(--color-text-secondary)] mt-1">
            Manage your profile and security settings
          </p>
        </div>

        {/* Profile Settings */}
        <Card className="shadow-layered border-0 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-[var(--color-accent)]" />
              </div>
              <div>
                <CardTitle className="font-dashboard-heading text-lg font-semibold">
                  Profile Information
                </CardTitle>
                <CardDescription>Update your business details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName" className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Business Name
                </Label>
                <Input
                  id="businessName"
                  type="text"
                  value={profile.businessName}
                  onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
                  required
                  data-testid="settings-business-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  required
                  data-testid="settings-email-input"
                  className="h-11"
                />
                <p className="text-xs text-[var(--color-text-muted)]">
                  Changing your email will require you to log in again
                </p>
              </div>
              
              <Button 
                type="submit" 
                disabled={savingProfile}
                data-testid="save-profile-btn"
                className="btn-lift"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Settings */}
        <Card className="shadow-layered border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="font-dashboard-heading text-lg font-semibold">
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  required
                  data-testid="current-password-input"
                  className="h-11"
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                  className="h-11"
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={savingPassword}
                data-testid="change-password-btn"
                className="btn-lift"
              >
                <Lock className="w-4 h-4 mr-2" />
                {savingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-[var(--color-text-muted)]">
            <strong>Account created:</strong> {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
